<template>
    <div class="media-container">

        <!-- Modern Header Section -->
        <header class="media-header">
            <div class="header-content">
                <div class="title-section">
                    <div class="page-title">
                        <div class="title-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                <circle cx="8.5" cy="8.5" r="1.5"/>
                                <polyline points="21 15 16 10 5 21"/>
                            </svg>
                        </div>
                        <div class="title-content">
                            <h1>Media Management</h1>
                            <p class="subtitle">Upload, organize, and manage your media content efficiently</p>
                        </div>
                    </div>
                </div>
                
                <div class="header-actions">
                    <button 
                        class="action-btn back" 
                        @click="goBack" 
                        aria-label="Go back"
                    >
                        <font-awesome-icon :icon="['fas', 'arrow-left']" />
                        <span>Back</span>
                    </button>
                    <button 
                        class="action-btn secondary" 
                        @click="refreshVideos"
                        :disabled="isFetching"
                        aria-label="Refresh media list"
                    >
                        <font-awesome-icon 
                            :icon="['fas', 'sync-alt']" 
                            :class="{ 'spinning': isFetching }"
                        />
                        <span>Refresh</span>
                    </button>
                    <button 
                        class="action-btn primary" 
                        @click="openUploadModal"
                        aria-label="Upload new video"
                    >
                        <font-awesome-icon :icon="['fas', 'plus']" />
                        <span>Upload Video</span>
                    </button>
                </div>
            </div>
        </header>

        <!-- Enhanced Content Area -->
        <main class="content-area">
            <!-- Stats Cards -->
            <div class="stats-grid" v-if="!isFetching">
                <div class="stat-card">
                    <div class="stat-icon">
                        <font-awesome-icon :icon="['fas', 'video']" />
                    </div>
                    <div class="stat-info">
                        <h3>{{ totalCount }}</h3>
                        <p>Total Videos</p>
                    </div>
                </div>
                <div class="stat-card">
                    <div class="stat-icon success">
                        <font-awesome-icon :icon="['fas', 'check-circle']" />
                    </div>
                    <div class="stat-info">
                        <h3>{{ completedCount }}</h3>
                        <p>Completed</p>
                    </div>
                </div>
                <div class="stat-card">
                    <div class="stat-icon processing">
                        <font-awesome-icon :icon="['fas', 'clock']" />
                    </div>
                    <div class="stat-info">
                        <h3>{{ processingCount }}</h3>
                        <p>Processing</p>
                    </div>
                </div>
            </div>

            <!-- Enhanced Table Container -->
            <div class="table-container">
                <!-- Table Loading Overlay -->
                <div class="table-loading-overlay" v-if="isFetching">
                    <div class="loading-content">
                        <div class="spinner"></div>
                        <p class="loading-text">Loading media content...</p>
                    </div>
                </div>
                
                <div class="table-header">
                    <h2>Media Files</h2>
                    <div class="table-info">
                        <span class="results-count">{{ totalCount }} results</span>
                    </div>
                </div>
                
                <div class="table-wrapper">
                    <!-- Header table (sticky) – same column widths as body table -->
                    <div v-if="videos.length > 0 && !isFetching" class="media-table-header-wrap">
                        <table class="media-table media-table-header">
                            <colgroup>
                                <col class="col-index">
                                <col class="col-processing-id">
                                <col class="col-status">
                                <col class="col-status">
                                <col class="col-platform">
                                <col class="col-date">
                                <col class="col-action">
                            </colgroup>
                            <thead>
                                <tr>
                                    <th class="col-index">#</th>
                                    <th class="col-processing-id">Processing ID</th>
                                    <th class="col-status">S3 Status</th>
                                    <th class="col-status">Platform Status</th>
                                    <th class="col-platform">Platform</th>
                                    <th class="col-date">Created</th>
                                    <th class="col-action">Actions</th>
                                </tr>
                            </thead>
                        </table>
                    </div>
                    <!-- Body table (scrolls) – identical column structure -->
                    <div class="media-table-body-wrap">
                        <table class="media-table media-table-body">
                            <colgroup>
                                <col class="col-index">
                                <col class="col-processing-id">
                                <col class="col-status">
                                <col class="col-status">
                                <col class="col-platform">
                                <col class="col-date">
                                <col class="col-action">
                            </colgroup>
                            <tbody>
                                <tr v-for="(video, index) in videos" :key="video.processingId" class="table-row">
                                    <td class="cell-index">{{ (currentPage - 1) * pageSize + index + 1 }}</td>
                                    <td class="cell-id">
                                        <code class="processing-id">{{ video.processingId }}</code>
                                    </td>
                                    <td class="cell-status">
                                        <span :class="['status-badge', getS3StatusClass(video.s3status)]">
                                            <span class="status-dot"></span>
                                            {{ video.s3status }}
                                        </span>
                                    </td>
                                    <td class="cell-status">
                                        <span v-if="video.platformstatus" :class="['status-badge', getPlatformStatusClass(video.platformstatus)]">
                                            <span class="status-dot"></span>
                                            {{ video.platformstatus }}
                                        </span>
                                        <span v-else class="empty-value">--</span>
                                    </td>
                                    <td class="cell-platform">
                                        <span v-if="video.platform" class="platform-tag">{{ video.platform }}</span>
                                        <span v-else class="empty-value">--</span>
                                    </td>
                                    <td class="cell-date">
                                        <time class="date-time">{{ formatDate(video.createdat) }}</time>
                                    </td>
                                    <td class="cell-action">
                                        <div class="action-buttons">
                                            <button
                                                class="action-icon-btn"
                                                :disabled="!video.videoUrl"
                                                @click="video.videoUrl && openVideoModal(video.videoUrl)"
                                                :title="video.videoUrl ? 'View video' : 'Video not available'"
                                                aria-label="View video"
                                            >
                                                <font-awesome-icon :icon="['fas', 'play-circle']" class="action-icon" />
                                            </button>
                                            <button
                                                class="action-icon-btn"
                                                :disabled="syncingIds.includes(video.processingId)"
                                                @click="syncVideo(video.processingId)"
                                                :title="syncingIds.includes(video.processingId) ? 'Syncing...' : 'Sync video'"
                                                aria-label="Sync video"
                                            >
                                                <font-awesome-icon
                                                    :icon="['fas', 'sync-alt']"
                                                    :class="['action-icon', { 'spinning': syncingIds.includes(video.processingId) }]"
                                                />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                                <tr v-if="videos.length === 0 && !isFetching" class="empty-row">
                                    <td colspan="7" class="empty-state">
                                        <div class="empty-content">
                                            <font-awesome-icon :icon="['fas', 'video']" class="empty-icon" />
                                            <h3>No Media Found</h3>
                                            <p>Upload your first video to get started</p>
                                            <button class="action-btn primary" @click="openUploadModal">
                                                <font-awesome-icon :icon="['fas', 'plus']" />
                                                <span>Upload Video</span>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <!-- Enhanced Pagination -->
                <nav class="pagination-nav" v-if="totalCount > pageSize" aria-label="Media pagination">
                    <div class="pagination-info">
                        <span>Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, totalCount) }} of {{ totalCount }} results</span>
                    </div>
                    <div class="pagination-controls">
                        <button 
                            :disabled="currentPage === 1 || isFetching" 
                            @click="changePage(currentPage - 1)"
                            class="pagination-btn nav-btn"
                            aria-label="Previous page"
                        >
                            <font-awesome-icon :icon="['fas', 'chevron-left']" />
                        </button>
                        
                        <div class="page-numbers">
                            <button 
                                v-for="page in visiblePages" 
                                :key="page"
                                :class="['pagination-btn', 'page-btn', { 'active': page === currentPage, 'ellipsis': page === '...' }]" 
                                :disabled="isFetching || page === '...'"
                                @click="changePage(page)"
                                :aria-label="page === '...' ? 'More pages' : `Go to page ${page}`"
                                :aria-current="page === currentPage ? 'page' : null"
                            >
                                {{ page }}
                            </button>
                        </div>
                        
                        <button 
                            :disabled="currentPage === totalPages || isFetching" 
                            @click="changePage(currentPage + 1)"
                            class="pagination-btn nav-btn"
                            aria-label="Next page"
                        >
                            <font-awesome-icon :icon="['fas', 'chevron-right']" />
                        </button>
                    </div>
                </nav>
            </div>
        </main>
    </div>
    
    <!-- Video view modal -->
    <div v-if="videoModalUrl" class="video-modal-overlay" @click.self="closeVideoModal">
        <div class="video-modal">
            <button class="video-modal-close" @click="closeVideoModal" aria-label="Close">
                <font-awesome-icon :icon="['fas', 'times']" />
            </button>
            <iframe
                :src="videoModalUrl"
                class="video-modal-iframe"
                frameborder="0"
                allowfullscreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            />
        </div>
    </div>
    
    <!-- Upload Modal -->
    <UploadMediaView v-if="showAddForm" @close="showAddForm = false" @success="handleAddVideo" />
</template>

<script>
import apiService from '@/services/api';
import { isNoDataError, isHmsSubscriptionInactiveError } from '../utilities/errorHelper';
import UploadMediaView from './UploadMediaView.vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import { 
    faSyncAlt, 
    faPlus, 
    faArrowLeft, 
    faVideo, 
    faCheckCircle, 
    faClock, 
    faChevronLeft, 
    faChevronRight,
    faPlayCircle,
    faTimes
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

library.add(faSyncAlt, faPlus, faArrowLeft, faVideo, faCheckCircle, faClock, faChevronLeft, faChevronRight, faPlayCircle, faTimes);

export default {
    name: 'MediaListView',
    components: {
        UploadMediaView,
        FontAwesomeIcon
    },
    data() {
        return {
            objectName: '',
            adminProfileData: {},
            showAddForm: false,
            showOnboardAddForm: false,
            videos: [],
            currentPage: 1,
            pageSize: 10,
            totalCount: 0,
            totalPages: 0,
            isFetching: false,
            objectId: '',
            cacheDuration: 5 * 60 * 1000, // Cache duration: 5 minutes in milliseconds
            objectSpaces: [],
            isLoading: false,
            syncingIds: [], // Track which videos are syncing
            videoModalUrl: null, // Video URL for view modal (when set, modal is open)
        };
    },
    computed: {
        visiblePages() {
            const range = 2;
            let pages = [];
            let start = Math.max(1, this.currentPage - range);
            let end = Math.min(this.totalPages, this.currentPage + range);

            if (start > 2) pages.push('...');
            for (let i = start; i <= end; i++) {
                pages.push(i);
            }
            if (end < this.totalPages - 1) pages.push('...');

            if (!pages.includes(1) && start > 1) pages.unshift(1);
            if (!pages.includes(this.totalPages) && end < this.totalPages) pages.push(this.totalPages);

            return pages;
        },
        completedCount() {
            return this.videos.filter(video => 
                video.s3status === 'COMPLETED' && video.platformstatus === 'COMPLETED'
            ).length;
        },
        processingCount() {
            return this.videos.filter(video => 
                video.s3status === 'PROCESSING' || 
                video.platformstatus === 'PROCESSING' || 
                video.platformstatus === 'PENDING'
            ).length;
        },
    },
    mounted() {
        this.objectName = this.$route.params.objectName;
        this.objectId = this.$route.params.id;
        let adminProfileData = localStorage.getItem('ADMIN_PROFILE_DATA');
        this.adminProfileData = JSON.parse(adminProfileData);
        // Force fresh API call on mount (page refresh)
        window.addEventListener('beforeunload', this.clearCache);
        this.fetchVideo();
        this.fetchObjectSpaces()
    },
    beforeUnmount() {
        // Clean up the event listener
        window.removeEventListener('beforeunload', this.clearCache);
    },
    methods: {
        goBack() {
            this.$router.go(-1);
        },
        openUploadModal() {
            // Refresh object spaces data before opening modal to ensure we have the latest data
            this.fetchObjectSpaces();
            this.showAddForm = true;
        },
        refreshVideos() {
            this.clearCache();       // Clear current cache
            this.fetchVideo(true);   // Force fresh fetch
        },
        openVideoModal(url) {
            if (!url) return;
            this.videoModalUrl = url;
        },
        closeVideoModal() {
            this.videoModalUrl = null;
        },
        handleAddVideo({ objectSpaceId, processingId }) {
            // Clear relevant cache
            const cacheKey = `VIDEO-${this.currentPage}-${this.pageSize}-${this.objectId}`;
            localStorage.removeItem(cacheKey);

            // Force fresh fetch
            this.fetchVideo(true);

            // Optionally log or use objectSpaceId/processingId
            console.log(`Uploaded video for space ${objectSpaceId}, processingId: ${processingId}`);
        },
        clearCache() {
            // Clear cache for the current page
            const cacheKey = `VIDEO-${this.currentPage}-${this.pageSize}-${this.objectId}`;
            localStorage.removeItem(cacheKey);
        },
        async fetchObjectSpaces() {
            const cacheKey = `OBJECTSPACE-${this.objectId}`;
            const cachedData = localStorage.getItem(cacheKey);
            // console.log('Cached Data:', cachedData);

            if (cachedData) {
                const { data, timestamp } = JSON.parse(cachedData);
                const now = new Date().getTime();
                if (now - timestamp < this.cacheDuration) {
                    // Use cached data
                    this.objectSpaces = data;
                    this.isLoading = false;
                    return;
                }
            }
            this.isLoading = true;
            try {
                let objectId = this.$route.params.id;
                let response = await apiService.getObjectSpace({
                    page: 1,
                    pagesize: 10,
                    objectType: this.adminProfileData.adminType,
                    objectId: objectId
                });
                // console.log('Object Spaces:', response.data);
                this.objectSpaces = response.data?.data || [];
                // Store response in cache with timestamp
                const cacheData = {
                    data: this.objectSpaces,
                    timestamp: new Date().getTime(),
                };
                localStorage.setItem(cacheKey, JSON.stringify(cacheData));

            } catch (error) {
                // Handle "no data" case - set empty array without showing error
                if (isNoDataError(error)) {
                    this.objectSpaces = [];
                } else if (isHmsSubscriptionInactiveError(error)) {
                    // Subscription inactive — modal shown in App.vue
                } else {
                    console.error('Failed to fetch object spaces:', error.response?.data);
                    // Handle error if needed
                }
            } finally {
                this.isLoading = false;
            }
        },
        async fetchVideo(forceFresh = false) {
            const cacheKey = `VIDEO-${this.currentPage}-${this.pageSize}-${this.objectId}`;
            
            // Check cache first
            if (!forceFresh) {
                const cachedData = this.getCachedData(cacheKey);
                if (cachedData) {
                    this.updateVideoData(cachedData);
                    return;
                }
            }

            // Fetch from API
            this.isFetching = true;
            try {
                const response = await apiService.getVideoProcessing(this.objectId, {
                    page: this.currentPage,
                    pagesize: this.pageSize
                });

                // Cache the response
                this.setCacheData(cacheKey, response.data);
                
                // Update component data
                this.updateVideoData(response.data);
            } catch (error) {
                // Handle "no data" case - set empty arrays without showing error
                if (isNoDataError(error)) {
                    this.videos = [];
                    this.totalCount = 0;
                    this.totalPages = 0;
                } else if (isHmsSubscriptionInactiveError(error)) {
                    // Subscription inactive — modal shown in App.vue
                } else {
                    console.error('Error fetching videos:', error);
                    this.handleFetchError(error);
                }
            } finally {
                this.isFetching = false;
            }
        },

        // Helper method to get cached data
        getCachedData(cacheKey) {
            try {
                const cachedData = localStorage.getItem(cacheKey);
                if (!cachedData) return null;

                const { data, timestamp } = JSON.parse(cachedData);
                const now = Date.now();
                
                if (now - timestamp < this.cacheDuration) {
                    return data;
                }
                
                // Remove expired cache
                localStorage.removeItem(cacheKey);
                return null;
            } catch (error) {
                console.warn('Error reading cache:', error);
                return null;
            }
        },

        // Helper method to set cache data
        setCacheData(cacheKey, data) {
            try {
                const cacheData = {
                    data,
                    timestamp: Date.now(),
                };
                localStorage.setItem(cacheKey, JSON.stringify(cacheData));
            } catch (error) {
                console.warn('Error setting cache:', error);
            }
        },

        // Helper method to update video data
        updateVideoData(data) {
            this.videos = data.data || [];
            this.totalCount = data.totalCount || 0;
            this.totalPages = Math.ceil(this.totalCount / this.pageSize);
        },


        // Helper method to handle fetch errors
        handleFetchError(error) {
            if (error.response?.status === 401) {
                // Handle unauthorized access
                console.error('Unauthorized access - redirecting to login');
                // You could emit an event or redirect here
            } else {
                console.error('Network error:', error.message);
            }
        },
        changePage(page) {
            if (
                (typeof page === 'number' && page >= 1 && page <= this.totalPages && !this.isFetching) ||
                (page === '...' && !this.isFetching)
            ) {
                this.currentPage = page;
                this.fetchVideo(); // Use cache for pagination
            }
        },
        formatDate(dateString) {
            if (!dateString) return '--';
            const date = new Date(dateString);
            return date.toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        },
        truncateText(text, maxLength) {
            if (!text || text.length <= maxLength) return text;
            return text.substring(0, maxLength) + '...';
        },
        getS3StatusClass(status) {
            switch (status) {
                case 'INITIATED': return 'badge-initiated';
                case 'COMPLETED': return 'badge-completed';
                case 'CANCELLED': return 'badge-cancelled';
                case 'FAILED': return 'badge-failed';
                default: return 'badge-default';
            }
        },
        getPlatformStatusClass(status) {
            switch (status) {
                case 'PENDING': return 'badge-pending';
                case 'PROCESSING': return 'badge-processing';
                case 'INITIATED': return 'badge-initiated';
                case 'COMPLETED': return 'badge-completed';
                case 'CANCELLED': return 'badge-cancelled';
                case 'FAILED': return 'badge-failed';
                default: return 'badge-default';
            }
        },
        async syncVideo(processingId) {
            if (this.syncingIds.includes(processingId)) return;
            
            this.syncingIds.push(processingId);
            
            try {
                await apiService.syncVideo(processingId);
                
                // Refresh the video list to show updated status
                await this.refreshVideos();
                
                // Show success feedback (you could replace with a toast notification)
                console.log(`Video ${processingId} synced successfully`);
                
            } catch (error) {
                console.error('Sync failed:', error);
                this.handleSyncError(error, processingId);
            } finally {
                this.syncingIds = this.syncingIds.filter(id => id !== processingId);
            }
        },

        // Helper method to handle sync errors
        handleSyncError(error, processingId) {
            if (error.response?.status === 404) {
                console.error(`Video ${processingId} not found`);
            } else if (error.response?.status === 409) {
                console.error(`Video ${processingId} is already syncing`);
            } else {
                console.error(`Failed to sync video ${processingId}:`, error.message);
            }
        },
    },
};
</script>

<style scoped>
/* Main Container */
.media-container {
    height: calc(100vh - 60px);
    background: #f8fafc;
    font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

/* Loading States */
.table-loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(2px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 100;
    border-radius: 12px;
}

.loading-content {
    text-align: center;
    color: #5b2e8e;
}

.loading-text {
    margin-top: 16px;
    font-size: 16px;
    font-weight: 500;
    color: #64748b;
}

.spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #e2e8f0;
    border-top: 4px solid #5b2e8e;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.spinning {
    animation: spin 1s linear infinite;
}

/* Header Styles */
.media-header {
    background: white;
    border-bottom: 1px solid #e2e8f0;
    padding: 16px 24px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    position: sticky;
    top: 0;
    z-index: 100;
    margin-top: 20px;
    flex-shrink: 0;
}

.header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
}

.title-section {
    flex: 1;
}

.page-title {
    text-align: left;
    display: flex;
    align-items: center;
    gap: 16px;
}

.title-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, #5b2e8e, #7c3aed);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 4px 12px rgba(91, 46, 142, 0.2);
}

.title-icon svg {
    width: 24px;
    height: 24px;
}

.title-content {
    flex: 1;
}

.page-title h1 {
    font-size: 20px;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 4px 0;
    line-height: 1.2;
    letter-spacing: -0.025em;
    text-align: left;
}

.subtitle {
    font-size: 14px;
    color: #64748b;
    margin: 0;
    line-height: 1.4;
    text-align: left;
}

.header-actions {
    display: flex;
    gap: 12px;
    align-items: center;
}

.action-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
    text-decoration: none;
    white-space: nowrap;
}

.action-btn.primary {
    background: linear-gradient(135deg, #5b2e8e, #7c3aed);
    color: white;
    box-shadow: 0 2px 6px rgba(91, 46, 142, 0.2);
}

.action-btn.primary:hover:not(:disabled) {
    background: linear-gradient(135deg, #4c1d75, #6b21d4);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(91, 46, 142, 0.3);
}

.action-btn.secondary {
    background: transparent;
    color: #64748b;
    border: 1px solid #e2e8f0;
}

.action-btn.secondary:hover:not(:disabled) {
    background: #f8fafc;
    border-color: #5b2e8e;
    color: #5b2e8e;
}

.action-btn.back {
    background: transparent;
    color: #64748b;
    border: 1px solid #e2e8f0;
}

.action-btn.back:hover:not(:disabled) {
    background: #f8fafc;
    border-color: #5b2e8e;
    color: #5b2e8e;
}

.action-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
}

/* Content Area */
.content-area {
    flex: 1;
    padding: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    min-height: 0;
}

/* Stats Grid */
.stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 12px;
    padding: 16px 24px;
    background: white;
    border-bottom: 1px solid #e2e8f0;
    flex-shrink: 0;
}

.stat-card {
    background: white;
    border-radius: 10px;
    padding: 14px 16px;
    display: flex;
    align-items: center;
    gap: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border: 1px solid #e2e8f0;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.stat-card:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.stat-icon {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #5b2e8e, #7c3aed);
    color: white;
    flex-shrink: 0;
    font-size: 18px;
}

.stat-icon.success {
    background: linear-gradient(135deg, #10b981, #059669);
}

.stat-icon.processing {
    background: linear-gradient(135deg, #f59e0b, #d97706);
}

.stat-info {
    flex: 1;
    min-width: 0;
    text-align: left;
}

.stat-info h3 {
    font-size: 18px;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 2px 0;
    line-height: 1.2;
}

.stat-info p {
    font-size: 12px;
    color: #64748b;
    margin: 0;
    font-weight: 500;
}

/* Table Container */
.table-container {
    background: white;
    border-radius: 0;
    box-shadow: none;
    border: none;
    overflow: hidden;
    padding: 0;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    position: relative;
}

.table-header {
    padding: 16px 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #e2e8f0;
    flex-shrink: 0;
    background: white;
}

.table-header h2 {
    font-size: 16px;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
}

.results-count {
    font-size: 14px;
    color: #64748b;
    font-weight: 500;
}

.table-wrapper {
    overflow-x: auto;
    overflow-y: auto;
    scrollbar-gutter: stable;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

/* Two-table layout: header table (sticky) + body table (scroll) so columns stay aligned */
.media-table-header-wrap {
    position: sticky;
    top: 0;
    z-index: 10;
    background: #fff;
    flex-shrink: 0;
    border-bottom: 2px solid #e2e8f0;
}

.media-table-body-wrap {
    flex: 1;
    min-height: 0;
    overflow: visible;
}

.media-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
    table-layout: fixed;
}

/* Same column widths for header and body tables (pixel values so both tables match exactly) */
.media-table col.col-index { width: 56px; }
.media-table col.col-processing-id { width: 180px; }
.media-table col.col-status { width: 120px; }
.media-table col.col-platform { width: 100px; }
.media-table col.col-date { width: 160px; }
.media-table col.col-action { width: 100px; }

.media-table th,
.media-table td {
    box-sizing: border-box;
    text-align: center;
}

.media-table-header th {
    background: #f8fafc;
    color: #374151;
    font-weight: 600;
    padding: 8px 10px;
    text-align: center;
    white-space: nowrap;
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: 0.025em;
}

.media-table-body td {
    padding: 8px 10px;
    border-bottom: 1px solid #f1f5f9;
    color: #475569;
    vertical-align: middle;
    font-size: 14px;
    white-space: nowrap;
    text-align: center;
}

.table-row:hover {
    background-color: #f8fafc;
}

.table-row:last-child td {
    border-bottom: none;
}

/* Table Cell Styles */
.cell-index {
    font-weight: 600;
    color: #64748b;
}

.cell-id {
    max-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
}

.cell-id code {
    display: inline-block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    background: #f1f5f9;
    padding: 2px 6px;
    border-radius: 6px;
    font-size: 12px;
    font-family: 'SF Mono', Monaco, monospace;
    color: #5b2e8e;
    font-weight: 500;
}

.empty-value {
    color: #94a3b8;
    font-style: italic;
}

.cell-platform .platform-tag {
    background: #ede9fe;
    color: #5b2e8e;
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
}

.cell-error .error-code {
    background: #fef2f2;
    color: #dc2626;
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
}

.reason-text {
    max-width: 200px;
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.cell-date {
    white-space: nowrap;
    min-width: 140px;
}

.date-time {
    color: #64748b;
    font-size: 14px;
    white-space: nowrap;
}

.user-name {
    font-weight: 500;
    color: #374151;
}

/* Empty State */
.empty-row td {
    padding: 48px 24px;
    text-align: center;
    border: none;
}

.empty-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
}

.empty-icon {
    font-size: 48px;
    color: #cbd5e1;
}

.empty-content h3 {
    font-size: 18px;
    font-weight: 600;
    color: #64748b;
    margin: 0;
}

.empty-content p {
    font-size: 14px;
    color: #94a3b8;
    margin: 0;
}

/* Action buttons in table cell */
.action-buttons {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
}

.action-icon-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    border-radius: 6px;
    transition: all 0.2s ease;
    color: #5b2e8e;
}

.action-icon-btn:hover:not(:disabled) {
    background: #f3f4f6;
    transform: scale(1.1);
}

.action-icon-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.action-icon {
    font-size: 16px;
}

/* Video view modal */
.video-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.75);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
    padding: 24px;
}

.video-modal {
    position: relative;
    width: 100%;
    max-width: 900px;
    aspect-ratio: 16 / 9;
    background: #000;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
}

.video-modal-close {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.9);
    color: #374151;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
    transition: background 0.2s;
}

.video-modal-close:hover {
    background: #fff;
}

.video-modal-iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
}

/* Status Badges */
.status-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    min-width: 90px;
    justify-content: center;
    text-transform: uppercase;
    letter-spacing: 0.025em;
}

.status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
}

.badge-initiated {
    background: #dbeafe;
    color: #1d4ed8;
    border: 1px solid #93c5fd;
}

.badge-completed {
    background: #d1fae5;
    color: #047857;
    border: 1px solid #86efac;
}

.badge-cancelled {
    background: #f3f4f6;
    color: #6b7280;
    border: 1px solid #d1d5db;
}

.badge-failed {
    background: #fee2e2;
    color: #dc2626;
    border: 1px solid #fca5a5;
}

.badge-pending {
    background: #fef3c7;
    color: #d97706;
    border: 1px solid #fcd34d;
}

.badge-processing {
    background: #cffafe;
    color: #0891b2;
    border: 1px solid #67e8f9;
}

.badge-default {
    background: #f1f5f9;
    color: #64748b;
    border: 1px solid #cbd5e1;
}


/* Pagination */
.pagination-nav {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 24px;
    border-top: 1px solid #e2e8f0;
    background: white;
    flex-shrink: 0;
}

.pagination-info {
    font-size: 14px;
    color: #64748b;
    font-weight: 500;
}

.pagination-controls {
    display: flex;
    align-items: center;
    gap: 8px;
}

.pagination-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 36px;
    height: 36px;
    padding: 0 8px;
    border: 1px solid #e2e8f0;
    background: white;
    color: #64748b;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    transition: all 0.2s ease;
}

.pagination-btn:hover:not(:disabled):not(.ellipsis) {
    border-color: #5b2e8e;
    color: #5b2e8e;
    background: #faf7ff;
}

.pagination-btn.active {
    background: #5b2e8e;
    border-color: #5b2e8e;
    color: white;
}

.pagination-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.pagination-btn.ellipsis {
    border: none;
    background: none;
    cursor: default;
}

.page-numbers {
    display: flex;
    gap: 4px;
}

.nav-btn {
    width: 36px;
}

/* Responsive Design */
@media (max-width: 1200px) {
    .content-area {
        padding: 24px 16px;
    }
    
    .header-content {
        padding: 0 16px;
    }
    
    .stats-grid {
        padding: 20px 16px;
        margin: 0 -16px 8px -16px;
    }
}

/* Screens below 720px */
@media (max-width: 720px) {
    .media-container {
        height: calc(100vh - 60px);
        overflow-x: hidden;
        overflow-y: hidden;
    }
    
    .content-area {
        padding: 0;
        overflow: hidden;
        min-height: 0;
        flex: 1;
        display: flex;
        flex-direction: column;
    }
    
    .media-header {
        padding: 12px 16px;
        margin-top: 12px;
        position: relative;
        flex-shrink: 0;
    }
    
    .header-content {
        flex-direction: column;
        gap: 12px;
        align-items: stretch;
    }
    
    .header-actions {
        flex-direction: row;
        flex-wrap: wrap;
        gap: 8px;
        width: 100%;
    }
    
    .action-btn {
        flex: 1;
        min-width: calc(50% - 4px);
        justify-content: center;
        padding: 8px 12px;
        font-size: 12px;
    }
    
    .title-icon {
        width: 40px;
        height: 40px;
    }

    .title-icon svg {
        width: 20px;
        height: 20px;
    }

    .page-title {
        gap: 12px;
    }

    .page-title h1 {
        font-size: 18px;
    }
    
    .subtitle {
        font-size: 12px;
        display: none;
    }
    
    /* Compact Stats */
    .stats-grid {
        grid-template-columns: repeat(3, 1fr);
        gap: 8px;
        padding: 12px 16px;
        margin: 0;
        flex-shrink: 0;
    }
    
    .stat-card {
        padding: 10px 12px;
        gap: 8px;
    }
    
    .stat-icon {
        width: 32px;
        height: 32px;
        font-size: 14px;
        flex-shrink: 0;
    }
    
    .stat-info h3 {
        font-size: 16px;
        margin: 0;
    }
    
    .stat-info p {
        font-size: 12px;
        margin: 0;
    }
    
    /* Table Container */
    .table-container {
        flex: 1;
        min-height: 0;
        max-height: none;
        overflow: hidden;
        display: flex;
        flex-direction: column;
    }
    
    .table-header {
        padding: 12px 16px;
        flex-wrap: wrap;
        gap: 8px;
        flex-shrink: 0;
    }
    
    .table-header h2 {
        font-size: 14px;
    }
    
    .results-count {
        font-size: 12px;
    }
    
    .table-wrapper {
        overflow-x: auto;
        overflow-y: auto;
        flex: 1;
        min-height: 0;
        -webkit-overflow-scrolling: touch;
    }
    
    .media-table {
        min-width: 1000px;
    }
    
    .media-table th,
    .media-table td {
        padding: 6px 8px;
        font-size: 12px;
    }
    
    .cell-date {
        white-space: nowrap;
        min-width: 120px;
    }
    
    .date-time {
        white-space: nowrap;
    }
    
    .pagination-nav {
        flex-direction: column;
        gap: 12px;
        align-items: stretch;
        padding: 12px 16px;
        flex-shrink: 0;
    }
    
    .pagination-info {
        font-size: 12px;
        text-align: center;
    }
    
    .pagination-controls {
        justify-content: center;
        flex-wrap: wrap;
    }
    
    .pagination-btn {
        min-width: 32px;
        height: 32px;
        font-size: 12px;
    }
}

@media (max-width: 768px) {
    .media-container {
        background: white;
    }
    
    .media-header {
        padding: 16px;
        margin-top: 10px;
    }
    
    .header-content {
        flex-direction: column;
        gap: 16px;
        align-items: stretch;
    }
    
    .header-actions {
        flex-direction: row;
        justify-content: stretch;
        flex-wrap: wrap;
    }
    
    .action-btn {
        flex: 1;
        min-width: 120px;
        justify-content: center;
    }
    
    .page-title h1 {
        font-size: 20px;
    }
    
    .content-area {
        padding: 0;
        gap: 0;
    }
    
    .stats-grid {
        grid-template-columns: repeat(3, 1fr);
        padding: 16px;
        margin: 0;
        gap: 10px;
    }
    
    .table-container {
        flex: 1;
        min-height: 0;
        max-height: none;
    }
    
    .table-wrapper {
        margin: 0;
        border-radius: 0;
        overflow-x: auto;
        overflow-y: auto;
        flex: 1;
        min-height: 0;
    }
    
    .table-header {
        padding: 16px;
    }
    
    .media-table th,
    .media-table td {
        padding: 6px 8px;
        font-size: 12px;
    }
    
    .pagination-nav {
        flex-direction: column;
        gap: 16px;
        align-items: stretch;
        text-align: center;
        padding: 16px;
    }
    
    .pagination-controls {
        justify-content: center;
    }
}

@media (max-width: 480px) {
    .stats-grid {
        grid-template-columns: repeat(3, 1fr);
        gap: 6px;
        padding: 10px 12px;
    }
    
    .stat-card {
        padding: 8px 10px;
        gap: 6px;
    }
    
    .stat-icon {
        width: 28px;
        height: 28px;
        font-size: 12px;
    }
    
    .stat-info h3 {
        font-size: 14px;
    }
    
    .stat-info p {
        font-size: 12px;
    }
    
    .media-header {
        padding: 10px 12px;
    }
    
    .title-icon {
        width: 36px;
        height: 36px;
    }

    .title-icon svg {
        width: 18px;
        height: 18px;
    }

    .page-title {
        gap: 10px;
    }

    .page-title h1 {
        font-size: 16px;
    }
    
    .table-header {
        padding: 10px 12px;
    }
    
    .media-table {
        min-width: 900px;
    }
    
    .media-table th,
    .media-table td {
        padding: 6px 6px;
        font-size: 12px;
    }
    
    .cell-date {
        white-space: nowrap;
        min-width: 110px;
    }
    
    .date-time {
        white-space: nowrap;
    }
    
    .status-badge {
        min-width: 60px;
        padding: 4px 6px;
        font-size: 12px;
    }
    
    .pagination-nav {
        padding: 10px 12px;
    }
    
    .pagination-info {
        font-size: 12px;
    }
    
    .pagination-btn {
        min-width: 28px;
        height: 28px;
        font-size: 12px;
        padding: 0 6px;
    }
}
</style>